/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./styles.css";
import DropdownArrow from "./images/dropdown.svg";
import { NUMBER } from "../../../constants/app-constants";

const SelectDLS = ({
    autoFocus = false,
    placeholder = "Select...",
    data = [],
    id,
    onChange = () => {},
    onBlurChange = () => {},
    variant = "large",
    scrollMenuIntoView = false,
    stopEventPropagation = false,
    ...restProps
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null); // 👈 added
    const { form = {}, field = {} } = restProps || {};
    const isSmallVariant = variant === "small";

    const { value = "", label = "" } = (data || []).find((obj) => obj.value === field.value) || {};

    const handleSelect = (val) => {
        onChange(val, restProps);
        if (restProps.isFormikForm) form.setFieldValue(id, val);
        setIsOpen(false);
        onChange(val);
        onBlurChange(val, restProps); // mimic blur on selection
    };

    const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setIsOpen(false);
            onBlurChange(value, restProps); // mimic blur on outside click
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        if (autoFocus) {
            // eslint-disable-next-line no-unused-expressions
            triggerRef.current?.focus();
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [autoFocus]);

    // 👇 Auto-scroll into view when menu opens
    useEffect(() => {
        if (isOpen && menuRef.current && scrollMenuIntoView) {
            const rect = menuRef.current.getBoundingClientRect();
            const menuHeight = data.length * NUMBER.TWENTY;

            if (rect.bottom > (window.innerHeight - menuHeight)) {
                // menu bottom is cut off → scroll just enough
                window.scrollBy({
                    top: rect.bottom - (window.innerHeight - menuHeight), // +8px padding
                    behavior: "smooth"
                });
            } else if (rect.top < 0) {
                // menu top is cut off
                window.scrollBy({
                    top: rect.top - menuHeight,
                    behavior: "smooth"
                });
            }
        }
    }, [isOpen]);

    return (
        <div styleName="styles.customDropdown" ref={dropdownRef}>
            <div
                styleName={`styles.dropdownTrigger ${isOpen ? "styles.open" : ""} ${isSmallVariant ? "styles.dropdownTriggerSmall" : ""}`}
                onClick={(e) => {
                    if (stopEventPropagation) e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                tabIndex={0}
                ref={triggerRef}
                id={id}
            >
                <p styleName={`styles.dropdownPlaceholder ${isSmallVariant ? "styles.dropdownPlaceholderSmall" : ""}`}>
                    {label || placeholder}
                </p>
                <span
                    style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                    styleName="dropdownArrow"
                >
                    <img src={DropdownArrow} alt="arrow-down" />
                </span>
            </div>

            {isOpen && (
                <ul styleName="dropdownOptions" ref={menuRef}>
                    {data.map(({ label: labelText, value: val }, index) => (
                        <li
                            key={val}
                            styleName={`styles.dropdownOption ${isSmallVariant ? "styles.dropdownOptionSmall" : ""} ${value === val ? "styles.selected" : ""} ${index !== 0 ? "styles.borderTop" : ""}`}
                            onClick={() => handleSelect(val)}
                        >
                            {labelText}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

SelectDLS.propTypes = {
    id: PropTypes.string.isRequired,
    placeHolder: PropTypes.string,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string
        })
    ).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlurChange: PropTypes.func,
    autoFocus: PropTypes.bool,
    variant: PropTypes.string,
    scrollMenuIntoView: PropTypes.bool,
    stopEventPropagation: PropTypes.bool,
    placeholder: PropTypes.string
};

export default SelectDLS;
